import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Search, Filter, ChevronDown, Eye, Edit2, UserCheck, UserX, 
  Trash2, LogIn, MoreVertical, Users, RefreshCw, Mail, MailCheck,
  UserMinus, UserPlus, AlertTriangle, Star, Building2, AlertOctagon
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TierBadge from '../components/TierBadge';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminManageUsers = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountTypes, setAccountTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  
  // Check if current user is main admin
  const isMainAdmin = user?.role && ['admin', 'main_admin'].includes(user.role);
  
  // Get initial filter values from URL
  const initialStatus = searchParams.get('status');
  const initialApprovalStatus = searchParams.get('approval_status');
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterAccountType, setFilterAccountType] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [filterTierStatus, setFilterTierStatus] = useState('all');
  const [filterUserStatus, setFilterUserStatus] = useState('all'); // Default: show ALL users
  const [filterApprovalStatus, setFilterApprovalStatus] = useState(initialApprovalStatus || initialStatus || 'all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [orderBy, setOrderBy] = useState('created_desc');
  
  // Action menu
  const [openActionMenu, setOpenActionMenu] = useState(null);
  
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  useEffect(() => {
    fetchData();
  }, [search, filterAccountType, filterTier, filterTierStatus, filterUserStatus, filterApprovalStatus, filterCompany, orderBy]);

  useEffect(() => {
    fetchAccountTypes();
    // Only fetch companies for main admins
    if (isMainAdmin) {
      fetchCompanies();
    }
  }, [isMainAdmin]);

  const fetchAccountTypes = async () => {
    try {
      const res = await api.get('/account-types');
      setAccountTypes(res.data);
    } catch (error) {
      console.error('Failed to fetch account types:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/main-admin/companies');
      setCompanies(res.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterAccountType !== 'all') params.append('account_type', filterAccountType);
      if (filterTier !== 'all') params.append('tier', filterTier);
      if (filterTierStatus !== 'all') params.append('tier_status', filterTierStatus);
      if (filterApprovalStatus !== 'all') params.append('approval_status', filterApprovalStatus);
      if (filterCompany !== 'all') params.append('company_id', filterCompany);
      params.append('user_status', filterUserStatus);
      params.append('order_by', orderBy);

      const res = await api.get(`/admin/users?${params.toString()}`);
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : '-';
  };

  const handleApprove = async (userId, tier) => {
    try {
      await api.put(`/admin/tiers/${userId}/approve`, { action: 'approve' });
      toast.success(`User's ${tier} tier approved`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
    setOpenActionMenu(null);
  };

  const handleReject = async (userId, tier) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      await api.put(`/admin/tiers/${userId}/approve`, { 
        action: 'reject',
        rejection_reason: reason || 'Application did not meet requirements'
      });
      toast.success(`User's ${tier} tier rejected`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
    setOpenActionMenu(null);
  };

  // User Account Approval (separate from tier approval)
  const handleApproveUser = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}`, { approval_status: 'approved' });
      toast.success('Account activated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate account');
    }
    setOpenActionMenu(null);
  };

  const handleRejectUser = async (userId) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      await api.put(`/admin/users/${userId}`, { 
        approval_status: 'rejected',
        rejection_reason: reason || 'Account activation request rejected'
      });
      toast.success('Account rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject account');
    }
    setOpenActionMenu(null);
  };

  const handleLoginAs = async (userId) => {
    try {
      const res = await api.post(`/admin/login-as/${userId}`);
      loginWithToken(res.data.access_token, res.data.user, true); // true = save admin token
      toast.success('Logged in as user. Click "Return to Admin" to go back.');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Failed to login as user');
    }
    setOpenActionMenu(null);
  };

  const handleToggleEmailVerified = async (userId, currentlyVerified) => {
    try {
      await api.put(`/admin/users/${userId}/verify-email`, { verified: !currentlyVerified });
      toast.success(currentlyVerified ? 'Email marked as unverified' : 'Email marked as verified');
      fetchData();
    } catch (error) {
      toast.error('Failed to update email verification status');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user "${userEmail}"?\n\nThis will permanently remove the user and all their related data. This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleHardDeleteUser = async (userId, userEmail) => {
    // Double confirmation for hard delete
    if (!window.confirm(`⚠️ PERMANENT DELETION WARNING ⚠️\n\nAre you absolutely sure you want to PERMANENTLY DELETE user "${userEmail}"?\n\nThis will remove:\n• User profile and login\n• All company data\n• All answers and documents\n• All support tickets\n• All deals and LOI requests\n• All activity logs\n• All invites and referrals\n\nThis action is IRREVERSIBLE!`)) {
      return;
    }
    
    // Second confirmation
    const confirmText = window.prompt(`Type "DELETE ${userEmail}" to confirm permanent deletion:`);
    if (confirmText !== `DELETE ${userEmail}`) {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }
    
    try {
      const res = await api.delete(`/admin/users/${userId}/hard-delete`);
      toast.success(`User permanently deleted. Removed data from: ${res.data.deleted_data?.deleted_collections?.join(', ') || 'all collections'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete user');
    }
  };

  const handleToggleUserStatus = async (userId, currentlyActive) => {
    const newStatus = !currentlyActive;
    const action = newStatus ? 'reactivate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }
    
    try {
      await api.put(`/admin/users/${userId}/status`, { is_active: newStatus });
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAccountType = (type) => {
    if (!type) return '-';
    return type.replace('_', '/').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTierStatusBadge = (tier, status) => {
    if (!tier || tier === 'none') {
      return <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">Unverified</span>;
    }
    
    if (status === 'pending') {
      return (
        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full animate-pulse">
          {tier.charAt(0).toUpperCase() + tier.slice(1)} (Pending)
        </span>
      );
    }
    
    return <TierBadge tier={tier} tierStatus={status} size="sm" />;
  };

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Manage Users</h1>
            <p className="text-gray-500 text-sm mt-1">View and manage all registered users</p>
          </div>
          <button
            onClick={fetchData}
            className="p-2.5 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters Row */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <FilterInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, country..."
              className="flex-1 min-w-[200px]"
              data-testid="search-users"
            />

            {/* Account Type Filter */}
            <FilterSelect
              value={filterAccountType}
              onChange={(e) => setFilterAccountType(e.target.value)}
              options={[
                { value: 'all', label: 'All Account Types' },
                { value: 'company_admin', label: 'Company Admin' },
                ...accountTypes.map(at => ({
                  value: at.name.toLowerCase().replace('/', '_').replace(' ', '_'),
                  label: at.name
                }))
              ]}
              data-testid="filter-account-type"
            />

            {/* Tier Filter */}
            <FilterSelect
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              options={[
                { value: 'all', label: 'All Tiers' },
                { value: 'none', label: 'Unverified' },
                { value: 'bronze', label: 'Bronze' },
                { value: 'silver', label: 'Silver' },
                { value: 'gold', label: 'Gold' }
              ]}
              data-testid="filter-tier"
            />

            {/* Status Filter */}
            <FilterSelect
              value={filterTierStatus}
              onChange={(e) => setFilterTierStatus(e.target.value)}
              options={[
                { value: 'all', label: 'All Tier Statuses' },
                { value: 'not_started', label: 'Not Started' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' }
              ]}
              data-testid="filter-tier-status"
            />

            {/* User Status Filter */}
            <FilterSelect
              value={filterUserStatus}
              onChange={(e) => setFilterUserStatus(e.target.value)}
              options={[
                { value: 'active', label: 'Active Users' },
                { value: 'inactive', label: 'Inactive Users' },
                { value: 'all', label: 'All Users' }
              ]}
              data-testid="filter-user-status"
            />

            {/* Approval Status Filter */}
            <FilterSelect
              value={filterApprovalStatus}
              onChange={(e) => setFilterApprovalStatus(e.target.value)}
              options={[
                { value: 'all', label: 'All Accounts' },
                { value: 'pending', label: 'Pending Activation' },
                { value: 'approved', label: 'Activated' },
                { value: 'rejected', label: 'Rejected' }
              ]}
              data-testid="filter-approval-status"
            />

            {/* Company Filter - Main Admin Only */}
            {isMainAdmin && (
              <FilterSelect
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                options={[
                  { value: 'all', label: 'All Companies' },
                  ...companies.map(company => ({ 
                    value: company.id, 
                    label: `${company.name}${company.is_main ? ' (Main)' : company.is_template ? ' (Template)' : ''}` 
                  }))
                ]}
                data-testid="filter-company"
              />
            )}

            {/* Sort */}
            <FilterSelect
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              options={[
                { value: 'created_desc', label: 'Newest First' },
                { value: 'created_asc', label: 'Oldest First' },
                { value: 'name_asc', label: 'Name A-Z' },
                { value: 'name_desc', label: 'Name Z-A' },
                { value: 'tier_desc', label: 'Tier (High to Low)' },
                { value: 'tier_asc', label: 'Tier (Low to High)' },
                { value: 'pending_first', label: 'Pending First' }
              ]}
              data-testid="sort-users"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-gray-50/80 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="text-left px-5 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Email</th>
                  <th className="text-left px-5 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Company</th>
                  <th className="text-left px-5 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Account Type</th>
                  <th className="text-left px-5 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Created</th>
                  <th className="text-left px-5 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-center px-5 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Loading users...
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-gray-500">
                      No users found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`user-row-${user.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {user.profile_photo ? (
                            <img 
                              src={user.profile_photo} 
                              alt="" 
                              className="w-9 h-9 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-sm font-medium">
                              {(user.first_name || user.email)?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {user.first_name && user.last_name 
                                ? `${user.first_name} ${user.last_name}`
                                : user.first_name || '-'}
                            </p>
                            {user.company_name && (
                              <p className="text-xs text-gray-500">{user.company_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{user.email}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {(user.role === 'company_admin' || user.role === 'admin') && (
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" title="Company Admin" />
                          )}
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm text-gray-600">{getCompanyName(user.company_id)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        {formatAccountType(user.account_type)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1">
                          {/* Account Approval Status badge */}
                          {user.approval_status === 'pending' && (
                            <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 w-fit bg-amber-100 text-amber-700">
                              <AlertTriangle className="w-3 h-3" />
                              Activation Requested
                            </span>
                          )}
                          {user.approval_status === 'rejected' && (
                            <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 w-fit bg-red-100 text-red-700">
                              <UserX className="w-3 h-3" />
                              Account Rejected
                            </span>
                          )}
                          {/* Inactive badge */}
                          {user.is_active === false && (
                            <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 w-fit bg-gray-200 text-gray-700">
                              <UserMinus className="w-3 h-3" />
                              Inactive
                            </span>
                          )}
                          {getTierStatusBadge(user.tier, user.tier_status)}
                          <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 w-fit ${
                            user.email_verified 
                              ? 'bg-green-50 text-green-700' 
                              : 'bg-red-50 text-red-600'
                          }`}>
                            {user.email_verified ? <MailCheck className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                            {user.email_verified ? 'Email Verified' : 'Email Not Verified'}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-1">
                          {/* View */}
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                            title="View Details"
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Edit */}
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                            className="p-2 text-gray-400 hover:rounded-lg transition"
                            style={{ '--hover-color': BRAND_BLUE }}
                            title="Edit User"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>

                          {/* Toggle Email Verified (not for admins) */}
                          {user.role !== 'admin' && (
                            <button
                              onClick={() => handleToggleEmailVerified(user.id, user.email_verified)}
                              className={`p-2 rounded-lg transition ${
                                user.email_verified 
                                  ? 'text-green-500 hover:text-red-600 hover:bg-red-50' 
                                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                              }`}
                              title={user.email_verified ? 'Mark Email Unverified' : 'Mark Email Verified'}
                              data-testid={`verify-email-${user.id}`}
                            >
                              {user.email_verified ? <MailCheck className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                            </button>
                          )}

                          {/* Approve (only show if pending) */}
                          {user.tier_status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(user.id, user.tier)}
                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                title={`Approve ${user.tier} Tier`}
                                data-testid={`approve-tier-${user.id}`}
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReject(user.id, user.tier)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                title={`Reject ${user.tier} Tier`}
                                data-testid={`reject-tier-${user.id}`}
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          {/* User Account Approval (show if approval_status is pending) */}
                          {user.approval_status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApproveUser(user.id)}
                                className="p-2 text-amber-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                                title="Activate Account"
                                data-testid={`approve-user-${user.id}`}
                              >
                                <UserPlus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRejectUser(user.id)}
                                className="p-2 text-amber-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Reject Account"
                                data-testid={`reject-user-${user.id}`}
                              >
                                <UserMinus className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          {/* Login As (not for admins) */}
                          {user.role !== 'admin' && (
                            <button
                              onClick={() => handleLoginAs(user.id)}
                              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
                              title="Login As User"
                              data-testid={`login-as-${user.id}`}
                            >
                              <LogIn className="w-4 h-4" />
                            </button>
                          )}

                          {/* Toggle Active/Inactive Status (not for admins) */}
                          {user.role !== 'admin' && (
                            <button
                              onClick={() => handleToggleUserStatus(user.id, user.is_active !== false)}
                              className={`p-2 rounded-lg transition ${
                                user.is_active === false 
                                  ? 'text-green-500 hover:text-green-700 hover:bg-green-50' 
                                  : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                              }`}
                              title={user.is_active === false ? 'Reactivate User' : 'Deactivate User'}
                              data-testid={`toggle-status-${user.id}`}
                            >
                              {user.is_active === false ? <UserPlus className="w-4 h-4" /> : <UserMinus className="w-4 h-4" />}
                            </button>
                          )}

                          {/* Delete User (not for admins) */}
                          {user.role !== 'admin' && (
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete User"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}

                          {/* Hard Delete User - Super Admin Only */}
                          {isMainAdmin && user.role !== 'admin' && (
                            <button
                              onClick={() => handleHardDeleteUser(user.id, user.email)}
                              className="p-2 text-gray-400 hover:text-red-700 hover:bg-red-100 rounded-lg transition"
                              title="Permanently Delete User & All Data"
                              data-testid={`hard-delete-user-${user.id}`}
                            >
                              <AlertOctagon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-sm text-gray-500 mt-4">
            Showing {users.length} user{users.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminManageUsers;
